import './AnnouncementBanner.scss';

import { useState } from 'react';
import { Notification } from '@myome/ui';

import { type Announcement, ANNOUNCEMENTS } from './announcements';

const DISMISSED_ANNOUNCEMENT_PREFIX = 'dismissed-announcement:';

const getDismissalKey = (announcementId: string) => `${DISMISSED_ANNOUNCEMENT_PREFIX}${announcementId}`;

const isAnnouncementDismissed = (announcementId: string) => {
  try {
    return localStorage.getItem(getDismissalKey(announcementId)) === 'true';
  } catch {
    return false;
  }
};

interface AnnouncementBannerProps {
  announcement: Announcement;
  onDismiss?: () => void;
}

interface AnnouncementBannersProps {
  announcements?: readonly Announcement[];
}

export const AnnouncementBanner = ({ announcement, onDismiss }: AnnouncementBannerProps) => {
  if (!announcement.enabled) return null;

  return (
    <Notification
      dismissible
      dismissalKey={getDismissalKey(announcement.id)}
      onDismiss={onDismiss}
      ariaLabel='Announcement'
      dismissLabel='Dismiss announcement'
      className='d-print-none'
    >
      {announcement.content}
    </Notification>
  );
};

export const AnnouncementBanners = ({ announcements = ANNOUNCEMENTS }: AnnouncementBannersProps) => {
  // Tracked here too so the padded wrapper goes away once every banner is dismissed.
  const [dismissedIds, setDismissedIds] = useState(() =>
    announcements.filter(announcement => isAnnouncementDismissed(announcement.id)).map(({ id }) => id),
  );

  const visibleAnnouncements = announcements.filter(
    announcement => announcement.enabled && !dismissedIds.includes(announcement.id),
  );

  if (visibleAnnouncements.length === 0) return null;

  const onDismiss = (announcementId: string) => {
    setDismissedIds(previous => [...previous, announcementId]);
  };

  return (
    <div className='announcement-banners d-print-none'>
      <div className='container-xxl announcement-banners__content'>
        {visibleAnnouncements.map(announcement => (
          <AnnouncementBanner
            key={announcement.id}
            announcement={announcement}
            onDismiss={() => onDismiss(announcement.id)}
          />
        ))}
      </div>
    </div>
  );
};
